import type { FieldValueType, WexlerStandardField } from './relativity-field-mapping.entity.js';

/**
 * Relativity field types as returned by the Relativity API
 * Based on Relativity Field Manager API documentation
 */
export enum RelativityFieldType {
  FixedLengthText = 'Fixed-Length Text',
  LongText = 'Long Text',
  WholeNumber = 'Whole Number',
  Decimal = 'Decimal',
  Currency = 'Currency',
  Date = 'Date',
  YesNo = 'Yes/No',
  SingleObject = 'Single Object',
  MultipleObject = 'Multiple Object',
  SingleChoice = 'Single Choice',
  MultipleChoice = 'Multiple Choice',
  User = 'User',
  File = 'File',
}

/**
 * Infer the FieldValueType from a Relativity field type.
 * This is used to dynamically determine how to store and query field values.
 *
 * @param relativityFieldType The Relativity field type string
 * @param isArray Whether the field is explicitly marked as an array (for Long Text fields)
 * @returns The appropriate FieldValueType for storage/querying
 */
export function inferFieldValueType(
  relativityFieldType: string,
  isArray?: boolean,
): FieldValueType {
  // If explicitly marked as array for Long Text, return string[]
  if (isArray === true && relativityFieldType === RelativityFieldType.LongText) {
    return 'string[]';
  }

  switch (relativityFieldType) {
    // Array types - stored as string[]
    case RelativityFieldType.MultipleChoice:
      return 'string[]';

    // Numeric types - stored as number
    case RelativityFieldType.WholeNumber:
    case RelativityFieldType.Decimal:
    case RelativityFieldType.Currency:
      return 'number';

    // All other types stored as string (including Date as ISO string in JSONB)
    case RelativityFieldType.SingleChoice:
    case RelativityFieldType.FixedLengthText:
    case RelativityFieldType.LongText:
    case RelativityFieldType.Date:
    case RelativityFieldType.YesNo:
    case RelativityFieldType.User:
    case RelativityFieldType.File:
    default:
      return 'string';
  }
}

/**
 * Type compatibility result
 */
export enum TypeCompatibility {
  Valid = 'valid', // Fully compatible, no issues
  Error = 'error', // Incompatible, cannot be mapped
}

export interface TypeValidationResult {
  compatible: TypeCompatibility;
  message: string | null;
}

/**
 * Validate type compatibility between Relativity field type and Wexler field.
 *
 * Simplified validation rules:
 * - Block Single Object and Multiple Object fields (cannot be mapped)
 * - Date fields (emailSentDate, documentDate) require Relativity Date type
 * - All other combinations are valid (type is inferred from Relativity field type)
 */
export function validateTypeCompatibility(
  relativityFieldType: string,
  wexlerField: WexlerStandardField | null,
): TypeValidationResult {
  // Block Single Object and Multiple Object entirely
  if (
    relativityFieldType === RelativityFieldType.SingleObject ||
    relativityFieldType === RelativityFieldType.MultipleObject
  ) {
    return {
      compatible: TypeCompatibility.Error,
      message:
        'Single Object and Multiple Object fields cannot be mapped. These fields reference other Relativity objects.',
    };
  }

  // Date fields (dedicated columns) require Relativity Date type
  if (wexlerField === 'emailSentDate' || wexlerField === 'documentDate') {
    if (relativityFieldType !== RelativityFieldType.Date) {
      return {
        compatible: TypeCompatibility.Error,
        message: 'Only Relativity Date fields can be mapped to Email Sent Date or Document Date.',
      };
    }
  }

  // All other combinations are valid
  return {
    compatible: TypeCompatibility.Valid,
    message: null,
  };
}

/**
 * Validate all field mappings in a configuration
 * Returns a map of field artifact IDs to validation results
 */
export function validateFieldMappings(mappings: {
  [relativityArtifactId: string]: {
    relativityFieldType: string;
    wexlerField: WexlerStandardField | null;
  };
}): Record<string, TypeValidationResult> {
  const results: Record<string, TypeValidationResult> = {};

  for (const [artifactId, config] of Object.entries(mappings)) {
    results[artifactId] = validateTypeCompatibility(config.relativityFieldType, config.wexlerField);
  }

  return results;
}

/**
 * Check if field mappings contain any errors (incompatible types)
 */
export function hasValidationErrors(
  validationResults: Record<string, TypeValidationResult>,
): boolean {
  return Object.values(validationResults).some(
    (result) => result.compatible === TypeCompatibility.Error,
  );
}

/**
 * Get all validation error messages
 */
export function getValidationErrorMessages(
  validationResults: Record<string, TypeValidationResult>,
): string[] {
  return Object.values(validationResults)
    .filter((result) => result.compatible === TypeCompatibility.Error && result.message !== null)
    .map((result) => result.message as string);
}
